import { type FC } from 'react';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import ChevronLeft from 'dibs-icons/exports/ChevronLeft.ts';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';
import { useHomePageContext } from '../HomePageContext.tsx';

import styles from './HpRecentlyViewedShimmer.scss';

const SHIMMER_ITEMS_DESKTOP = 6;
const SHIMMER_ITEMS_TABLET = 5;

const DesktopShimmer: FC = () => {
    const { isTablet } = useHomePageContext();
    const shimmerItemCount = isTablet ? SHIMMER_ITEMS_TABLET : SHIMMER_ITEMS_DESKTOP;

    return (
        <div className={dibsCss.pyLarge}>
            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.gapLarge,
                    dibsCss.wFull,
                    dibsCss.itemsCenter
                )}
            >
                <div
                    className={classnames(dibsCss.flexShrink0, {
                        [dibsCss.w200px]: !isTablet,
                    })}
                    style={isTablet ? { width: 160 } : undefined}
                >
                    <div
                        className={classnames(
                            styles.shimmer,
                            styles.shimmerTitle,
                            dibsCss.mbXsmall
                        )}
                    />
                    <div className={classnames(styles.shimmer, styles.shimmerLink)} />
                </div>
                <div
                    className={classnames(
                        dibsCss.flex1,
                        dibsCss.minW0,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.gapMedium
                    )}
                >
                    <div className={classnames(dibsCss.pXsmall, dibsCss.pr0, dibsCss.flexShrink0)}>
                        <ChevronLeft variation="40" />
                    </div>
                    <div
                        className={classnames(dibsCss.flex1, dibsCss.minW0, dibsCss.overflowHidden)}
                    >
                        <div
                            className={classnames(
                                styles.itemsWrapper,
                                dibsCss.flex,
                                dibsCss.overflowHidden,
                                dibsCss.gapMedium
                            )}
                        >
                            {Array.from({ length: shimmerItemCount }, (_, index) => (
                                <div
                                    key={index}
                                    className={classnames(
                                        dibsCss.flex1,
                                        dibsCss.minW0,
                                        dibsCss.hFull
                                    )}
                                >
                                    <div
                                        className={classnames(
                                            styles.shimmer,
                                            dibsCss.wFull,
                                            dibsCss.hFull
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={classnames(dibsCss.pXsmall, dibsCss.pl0, dibsCss.flexShrink0)}>
                        <ChevronRight variation="40" />
                    </div>
                </div>
            </div>
        </div>
    );
};

const MobileShimmer: FC = () => (
    <div
        className={classnames(
            'primary-viewport-width',
            dibsCss.pyLarge,
            dibsCss.ptMedium,
            dibsCss.pbMedium
        )}
    >
        <div className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.gapSmall, dibsCss.wFull)}>
            <div
                className={classnames(
                    dibsCss.plSmall,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.gapXxsmall
                )}
            >
                <div className={classnames(styles.shimmer, styles.shimmerTitleMobile)} />
                <ChevronRight variation="24" />
            </div>
            <div className={classnames(dibsCss.flex1, dibsCss.minW0, dibsCss.overflowHidden)}>
                <div
                    className={classnames(
                        styles.itemsWrapper,
                        dibsCss.flex,
                        dibsCss.overflowHidden,
                        dibsCss.plSmall,
                        dibsCss.gapSmall
                    )}
                >
                    {[0, 1, 2].map(index => (
                        <div
                            key={index}
                            className={classnames(styles.itemMobile, dibsCss.flexShrink0)}
                        >
                            <div
                                className={classnames(styles.shimmer, dibsCss.wFull, dibsCss.hFull)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

type Props = {
    isMobile: boolean;
};

export const HpRecentlyViewedShimmer: FC<Props> = ({ isMobile }) =>
    isMobile ? <MobileShimmer /> : <DesktopShimmer />;
